import message from "antd/lib/message";
import { useEffect, EffectCallback } from "react";
import {
  useDispatch as rrUseDispatch,
  useSelector as rrUseSelector,
  shallowEqual,
  TypedUseSelectorHook,
} from "react-redux";

import type { SerializedError } from "@skydio/redux_util/src";

export const useMount = (effect: EffectCallback) => useEffect(effect, []);

type GetStateType<TStore> = TStore extends { getState: () => infer TState } ? TState : unknown;
type GetDispatchType<TStore> = TStore extends { dispatch: infer TDispatch } ? TDispatch : unknown;

// Create specifically typed (app-specific) versions of the react-redux hooks
export const createReduxHooks = <
  TStore,
  TState = GetStateType<TStore>,
  TDispatch = GetDispatchType<TStore>,
>(
  _store?: TStore
) => {
  // Use this for most selectors
  const useSelector: TypedUseSelectorHook<TState> = rrUseSelector;
  // Use this for selectors that return an object rather than a single value if you want shallow
  // instead of strict equality checking
  const useShallowSelector: TypedUseSelectorHook<TState> = selector =>
    rrUseSelector(selector, shallowEqual);

  const useDispatch = () => rrUseDispatch<TDispatch>();

  return { useSelector, useShallowSelector, useDispatch };
};

// Use this to create an antd message popup when the provided success param is true
export const useSuccessNotification = (success: boolean, successMessage: string) => {
  useEffect(() => {
    // Only display if provided a real message
    if (success && successMessage) {
      // Reset any previous message just in case, otherwise there can be weird duplicate behavior
      message.destroy();
      message.success(successMessage);
    }
  }, [success, successMessage]);
};

// Use this to create an antd message popup when the provided error is non-null
export const useErrorNotification = (error: SerializedError | null) => {
  useEffect(() => {
    if (error) {
      message.destroy();
      message.error(error.message);
    }
  }, [error]);
};
